import { getWebPath } from '@doss/common-all'
import { ChevronRight } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { cn } from '~/@/lib/utils'
import { BreadcrumbObjectType } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'

/**
 * A blocking reference from one row to another, with relationship context.
 */
export type BlockingReferenceDisplay = {
  /** The row that is blocking deletion */
  referencingRowId: string
  referencingRowName: string
  /** The row that cannot be deleted */
  blockedRowName: string
  /** Column and table context */
  columnName: string
  tableName: string
  tableId: string
}

interface BlockingReferencesDisplayProps {
  /** All blocking references to display */
  references: BlockingReferenceDisplay[]
  /** Optional header message (defaults to translation key based on count) */
  headerMessage?: string
  /** Optional footer message (defaults to 'remove_references_first' translation) */
  footerMessage?: string
  /** Whether to show the blocked row name in each reference line */
  showBlockedRowName?: boolean
  /** Max height of the scrollable area */
  maxHeight?: string
}

/**
 * Collapsible section for a table's blocking references.
 */
const CollapsibleTableSection = ({
  tableName,
  referenceCount,
  defaultOpen,
  children,
}: {
  tableName: string
  referenceCount: number
  defaultOpen: boolean
  children: ReactNode
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="ps-2">
      <button
        className="flex w-full items-center gap-1 py-1 text-left font-medium text-gray-12 hover:text-gray-11"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 transition-transform duration-150',
            isOpen && 'rotate-90',
          )}
        />
        <span className="truncate">{tableName}</span>
        <span className="text-xs font-normal text-gray-11">({referenceCount})</span>
      </button>
      {isOpen && <div className="ps-4 pt-1">{children}</div>}
    </div>
  )
}

/**
 * Groups blocking references by table and displays them in a collapsible format.
 * Used by both DeleteRecordDialog (single row) and BulkActionsMenu (bulk delete errors).
 */
export const BlockingReferencesDisplay = ({
  references,
  headerMessage,
  footerMessage,
  showBlockedRowName = true,
  maxHeight = 'max-h-72',
}: BlockingReferencesDisplayProps) => {
  const { t } = useTypedTranslation()

  // Group references by table
  const referencesByTable = new Map<
    string,
    {
      tableName: string
      refs: Array<{
        referencingRowId: string
        referencingRowName: string
        blockedRowName: string
        columnName: string
      }>
    }
  >()

  for (const ref of references) {
    const existing = referencesByTable.get(ref.tableId)
    if (existing) {
      existing.refs.push({
        referencingRowId: ref.referencingRowId,
        referencingRowName: ref.referencingRowName,
        blockedRowName: ref.blockedRowName,
        columnName: ref.columnName,
      })
    } else {
      referencesByTable.set(ref.tableId, {
        tableName: ref.tableName,
        refs: [
          {
            referencingRowId: ref.referencingRowId,
            referencingRowName: ref.referencingRowName,
            blockedRowName: ref.blockedRowName,
            columnName: ref.columnName,
          },
        ],
      })
    }
  }

  const tableEntries = Array.from(referencesByTable.entries())
  const hasMultipleTables = tableEntries.length > 1

  // Count unique blocked rows for the header
  const uniqueBlockedRows = new Set(references.map((r) => r.blockedRowName))
  const blockedCount = uniqueBlockedRows.size

  const defaultHeader =
    blockedCount === 1
      ? t('table.bulk_actions.rows_blocked_one', { count: blockedCount })
      : t('table.bulk_actions.rows_blocked_other', { count: blockedCount })

  const defaultFooter = t('record.remove_references_first')

  return (
    <div className="space-y-3 text-sm">
      <p className="font-medium text-red-11">{headerMessage ?? defaultHeader}</p>
      <div className={cn(maxHeight, 'space-y-2 overflow-y-auto border-l-2 border-red-6')}>
        {tableEntries.map(([tableId, { tableName, refs }], index) => (
          <CollapsibleTableSection
            key={tableId}
            defaultOpen={!hasMultipleTables || index === 0}
            referenceCount={refs.length}
            tableName={tableName}
          >
            <ul className="list-disc space-y-0.5 ps-4 text-gray-11">
              {refs.map((ref, idx) => (
                <li key={`${ref.referencingRowId}-${idx}`}>
                  <a
                    className="text-blue-11 underline"
                    href={getWebPath({
                      objectType: BreadcrumbObjectType.TABLE_ROW,
                      objectId: ref.referencingRowId,
                    })}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {ref.referencingRowName}
                  </a>
                  {ref.columnName && <span className="text-gray-10"> ({ref.columnName})</span>}
                  {showBlockedRowName && (
                    <>
                      {t('table.bulk_actions.blocks')}
                      <span className="font-medium">{ref.blockedRowName}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </CollapsibleTableSection>
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-11">{footerMessage ?? defaultFooter}</p>
    </div>
  )
}
